// =============================================================================
// MessagesWidget — notes pinned to units, surfaced as thread previews.
//
// Reads any `note` / `comment` entry in each unit's history and shows the
// most recent per unit. Click → open the unit's check-in.
// Drop-in replacement target for the Supabase messaging wire-up later.
// =============================================================================
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useBatteryUnits, relativeWhen, unitName, authorName, authorInitials } from "../lib/useBatteryData.js";
import { useAuth } from "../auth/AuthProvider.jsx";

export function MessagesWidget({ config }) {
  const nav = useNavigate();
  const { profile } = useAuth();
  const limit = config?.limit || 6;
  const { data: units, loading } = useBatteryUnits();
  const authorOpts = useMemo(
    () => ({ self: { uid: profile?.uid, displayName: profile?.displayName } }),
    [profile?.uid, profile?.displayName]
  );

  const threads = useMemo(() => {
    const out = [];
    for (const u of units) {
      const h = Array.isArray(u.history) ? u.history : [];
      // Newest note / comment for this unit, if any.
      for (let i = h.length - 1; i >= 0; i--) {
        const e = h[i];
        const a = String(e.action || "").toLowerCase();
        if (a.includes("note") || a.includes("comment") || (e.notes && String(e.notes).trim())) {
          out.push({ unit: u, e });
          break;
        }
      }
    }
    out.sort((a, b) => (b.e.at?.toMillis?.() || 0) - (a.e.at?.toMillis?.() || 0));
    return out.slice(0, limit);
  }, [units, limit]);

  return (
    <div className="w-shell">
      <header className="w-head">
        <div className="w-eye"><span className="w-eye-dot" />MESSAGES · PINNED TO UNITS</div>
        <h3 className="w-h3">Threads <em>worth seeing</em>.</h3>
      </header>

      {loading && <div className="w-loading">Loading threads…</div>}

      {!loading && threads.length === 0 && (
        <div className="w-empty">
          <div className="w-empty-h">No messages pinned yet.</div>
          <div className="w-empty-s">Notes left on a unit's check-in surface here so they don't get lost.</div>
        </div>
      )}

      {!loading && threads.length > 0 && (
        <div className="w-msg-list">
          {threads.map(({ unit, e }) => {
            const who = authorName(e.by, authorOpts);
            const initials = authorInitials(e.by, authorOpts);
            const text = String(e.notes || e.text || e.message || "").slice(0, 140);
            return (
              <button
                key={`${unit.id}-${e.at?.seconds || e.at?.toMillis?.() || Math.random()}`}
                type="button"
                className="w-msg"
                onClick={() => nav(`/check-in/${unit.id}`)}
              >
                <div className="w-msg-avi">{initials}</div>
                <div className="w-msg-body">
                  <div className="w-msg-head">
                    <span className="w-msg-who">{who}</span>
                    <span className="w-msg-when">{relativeWhen(e.at)}</span>
                  </div>
                  <div className="w-msg-text">{text || "—"}</div>
                  <div className="w-msg-ctx">pinned to {unit.stockNumber || unitName(unit)}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

